import { useEffect, useState } from "react";
import client, { mediaUrl } from "@/lib/api";
import { SectionHeader, notifyError, notifyOk } from "./_ui";
import HomeEditor from "./HomeEditor";
import SpecFieldsEditor from "./SpecFieldsEditor";
import FooterEditor from "./FooterEditor";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

function Field({ label, hint, children }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      {children}
      {hint && <p className="text-xs text-neutral-400">{hint}</p>}
    </div>
  );
}

const THEME_FIELDS = [
  ["accent", "Акцент"], ["ink", "Текст"], ["muted", "Приглушённый"],
  ["line", "Линии"], ["bg", "Фон"],
];

export default function SiteSettings() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    client.get("/admin/settings").then((r) => setS(r.data)).catch(notifyError);
  }, []);

  if (!s) return <div className="text-sm text-neutral-500">Загрузка…</div>;

  const setField = (k, v) => setS({ ...s, [k]: v });
  const setTheme = (k, v) => setS({ ...s, theme: { ...s.theme, [k]: v } });
  const setLabel = (k, v) => setS({ ...s, labels: { ...s.labels, [k]: v } });

  const uploadWm = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await client.post("/admin/watermark", fd);
      setField("watermark_image_url", data.url);
      notifyOk("Изображение загружено");
    } catch (e) { notifyError(e); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        site_name: s.site_name, act_title: s.act_title, verify_note: s.verify_note,
        labels: s.labels, theme: s.theme, watermark_text: s.watermark_text,
        watermark_image_url: s.watermark_image_url, act_number_format: s.act_number_format,
        home: s.home, spec_fields: s.spec_fields, footer: s.footer,
      };
      await client.put("/admin/settings", payload);
      notifyOk("Настройки сохранены");
    } catch (e) { notifyError(e); }
    finally { setSaving(false); }
  };

  return (
    <div className="max-w-2xl">
      <SectionHeader
        title="Настройки сайта"
        description="Названия, тексты, цвета, шрифт, водяной знак и формат номера акта."
        action={
          <Button data-testid="settings-save-btn" onClick={save} disabled={saving} style={{ background: "var(--ci-accent)", color: "#1a1a1a" }}>
            {saving ? "Сохранение…" : "Сохранить"}
          </Button>
        }
      />

      <Tabs defaultValue="general">
        <TabsList className="mb-6">
          <TabsTrigger value="general" data-testid="settings-tab-general">Тексты и тема</TabsTrigger>
          <TabsTrigger value="home" data-testid="settings-tab-home">Главная</TabsTrigger>
          <TabsTrigger value="specfields" data-testid="settings-tab-specfields">Поля бота</TabsTrigger>
          <TabsTrigger value="footer" data-testid="settings-tab-footer">Футер / Контакты</TabsTrigger>
        </TabsList>

        <TabsContent value="general">
          <div className="space-y-8">
        <div className="border border-neutral-200 rounded-lg bg-white p-5 space-y-4">
          <h3 className="font-semibold text-sm">Тексты</h3>
          <Field label="Название проекта"><Input data-testid="set-site-name" value={s.site_name || ""} onChange={(e) => setField("site_name", e.target.value)} className="border-neutral-300" /></Field>
          <Field label="Заголовок акта"><Input data-testid="set-act-title" value={s.act_title || ""} onChange={(e) => setField("act_title", e.target.value)} className="border-neutral-300" /></Field>
          <Field label="Пояснение о проверке (подвал акта)"><Textarea data-testid="set-verify-note" value={s.verify_note || ""} onChange={(e) => setField("verify_note", e.target.value)} className="border-neutral-300" rows={2} /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Лейбл: данные авто"><Input data-testid="set-label-vehicle" value={s.labels?.vehicle || ""} onChange={(e) => setLabel("vehicle", e.target.value)} className="border-neutral-300" /></Field>
            <Field label="Лейбл: комплектация"><Input data-testid="set-label-equipment" value={s.labels?.equipment || ""} onChange={(e) => setLabel("equipment", e.target.value)} className="border-neutral-300" /></Field>
          </div>
          <Field label="Формат номера акта" hint="Плейсхолдеры: {YYYY} — год, {seq:06d} — порядковый номер">
            <Input data-testid="set-act-format" value={s.act_number_format || ""} onChange={(e) => setField("act_number_format", e.target.value)} className="border-neutral-300 font-mono" />
          </Field>
        </div>

        <div className="border border-neutral-200 rounded-lg bg-white p-5 space-y-4">
          <h3 className="font-semibold text-sm">Оформление (тема)</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {THEME_FIELDS.map(([k, label]) => (
              <Field key={k} label={label}>
                <div className="flex gap-2 items-center">
                  <input type="color" value={s.theme?.[k] || "#000000"} onChange={(e) => setTheme(k, e.target.value)} className="w-9 h-9 rounded border border-neutral-300 p-0.5" data-testid={`set-theme-${k}-color`} />
                  <Input data-testid={`set-theme-${k}`} value={s.theme?.[k] || ""} onChange={(e) => setTheme(k, e.target.value)} className="border-neutral-300 font-mono text-xs" />
                </div>
              </Field>
            ))}
          </div>
          <Field label="Шрифт (CSS font-family)"><Input data-testid="set-theme-font" value={s.theme?.font || ""} onChange={(e) => setTheme("font", e.target.value)} className="border-neutral-300 text-xs" /></Field>
          <Field label="Прозрачность водяного знака" hint="0 – 1"><Input data-testid="set-theme-wmop" value={s.theme?.watermark_opacity || ""} onChange={(e) => setTheme("watermark_opacity", e.target.value)} className="border-neutral-300 w-24" /></Field>
        </div>

        <div className="border border-neutral-200 rounded-lg bg-white p-5 space-y-4">
          <h3 className="font-semibold text-sm">Водяной знак</h3>
          <p className="text-xs text-neutral-500">Если оба поля пусты — водяной знак на акте не отображается.</p>
          <Field label="Текст водяного знака"><Input data-testid="set-wm-text" value={s.watermark_text || ""} onChange={(e) => setField("watermark_text", e.target.value)} className="border-neutral-300" /></Field>
          <Field label="Или изображение">
            <input data-testid="set-wm-file" type="file" accept="image/*" onChange={(e) => uploadWm(e.target.files[0])} className="text-sm" />
            {s.watermark_image_url && (
              <div className="flex items-center gap-3 mt-2">
                <img src={mediaUrl(s.watermark_image_url)} alt="" className="max-h-16 object-contain" />
                <Button variant="outline" size="sm" onClick={() => setField("watermark_image_url", "")} data-testid="set-wm-clear">Убрать</Button>
              </div>
            )}
          </Field>
        </div>
          </div>
        </TabsContent>

        <TabsContent value="home">
          <HomeEditor home={s.home} onChange={(v) => setField("home", v)} />
        </TabsContent>

        <TabsContent value="specfields">
          <div className="max-w-3xl">
            <SpecFieldsEditor fields={s.spec_fields} onChange={(v) => setField("spec_fields", v)} />
          </div>
        </TabsContent>

        <TabsContent value="footer">
          <FooterEditor footer={s.footer} onChange={(v) => setField("footer", v)} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
